/* ============================================================
 * AYU /buch – Buchverkaufsseite
 * "Der AYU – Tradition trifft Genetik"
 * DE + EN Versionen, E-Book (19,90€) + Print (24,90€)
 * ============================================================ */
import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { IMAGES } from "@/lib/constants";
import { useI18n } from "@/contexts/I18nContext";
import {
  BookOpen, Download, Truck, Globe, Star,
  ArrowRight, ChevronDown, ShieldCheck, Sparkles,
  Quote, Heart, Brain, Dna, Users
} from "lucide-react";

/* ── Intersection Observer Hook ── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function AnimatedSection({ children, delay = 0, className = "" }: {
  children: React.ReactNode; delay?: number; className?: string;
}) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ── Book Data ── */
const BOOK_COVERS = {
  de: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/buch-cover-de_083abd69.png",
  en: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/buch-cover-en_e18a1b95.png",
};

const STRIPE_LINKS = {
  print: "https://buy.stripe.com/fZudR88dX7NIail4nQ3sI00",
  ebookDe: "https://buy.stripe.com/dRm14m51L6JE0HL9Ia3sI0f",
  ebookEn: "https://buy.stripe.com/8x2aEW0Lvd821LP5rU3sI0g",
};

const BOOK_INFO = {
  de: {
    title: "Der AYU",
    subtitle: "Tradition trifft Genetik: Warum die Ayurveda-Industrie Europa krank macht",
    authors: "A. & C. Hanfstein",
  },
  en: {
    title: "The AYU",
    subtitle: "Tradition meets genetics: Why the Ayurveda industry is making Europe sick",
    authors: "A. & C. Hanfstein",
  },
};

const CHAPTERS = [
  { icon: Globe, titleKey: "buch.kapitel1Title" as const, descKey: "buch.kapitel1Desc" as const },
  { icon: Dna, titleKey: "buch.kapitel2Title" as const, descKey: "buch.kapitel2Desc" as const },
  { icon: Brain, titleKey: "buch.kapitel3Title" as const, descKey: "buch.kapitel3Desc" as const },
  { icon: Heart, titleKey: "buch.kapitel4Title" as const, descKey: "buch.kapitel4Desc" as const },
];

const REVIEWS = [
  {
    textKey: "buch.review1Text" as const,
    author: "Maria K.",
    rating: 5,
  },
  {
    textKey: "buch.review2Text" as const,
    author: "Thomas R.",
    rating: 5,
  },
  {
    textKey: "buch.review3Text" as const,
    author: "Sandra M.",
    rating: 5,
  },
];

export default function Buch() {
  const { t, locale, setLocale } = useI18n();
  const selectedLang = locale as "de" | "en";
  const [selectedFormat, setSelectedFormat] = useState<"ebook" | "print">("ebook");

  const getStripeLink = () => {
    if (selectedFormat === "print") return STRIPE_LINKS.print;
    return selectedLang === "de" ? STRIPE_LINKS.ebookDe : STRIPE_LINKS.ebookEn;
  };

  const getPrice = () => {
    return selectedFormat === "ebook" ? "19,90" : "24,90";
  };

  return (
    <div className="min-h-screen bg-[oklch(0.98_0.005_85)]">

      {/* ── Sticky Nav ── */}
      <nav className="fixed top-0 w-full z-50 bg-[oklch(0.12_0.02_65)/0.95] backdrop-blur-md border-b border-gold/10">
        <div className="container max-w-6xl mx-auto flex items-center justify-between h-16 px-4">
          <Link href="/" className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-8 w-8" />
            <span className="font-playfair text-lg text-[oklch(0.92_0.02_85)]">AYU</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link href="/termin" className="text-sm text-[oklch(0.7_0.02_85)] hover:text-gold transition-colors">
              {t("buch.navConsultation")}
            </Link>
            <a
              href="#kaufen"
              className="px-5 py-2 rounded-full bg-gold text-[oklch(0.15_0.02_65)] text-sm font-semibold hover:bg-gold-light transition-colors"
            >
              {t("buch.navBuy")}
            </a>
          </div>
        </div>
      </nav>

      {/* ========== HERO ========== */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-[oklch(0.12_0.02_65)] via-[oklch(0.15_0.02_65)] to-[oklch(0.98_0.005_85)]">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: Book Cover */}
            <AnimatedSection className="flex justify-center">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute -inset-8 bg-gold/10 rounded-3xl blur-3xl" />
                <div className="relative">
                  <img
                    src={BOOK_COVERS[selectedLang]}
                    alt={`${BOOK_INFO[selectedLang].title} - Cover`}
                    className="w-full max-w-md rounded-lg shadow-2xl"
                  />
                  {/* Language Toggle on Cover */}
                  <div className="absolute top-4 right-4 flex gap-1 bg-black/60 backdrop-blur-sm rounded-full p-1">
                    <button
                      onClick={() => setLocale("de")}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                        selectedLang === "de"
                          ? "bg-gold text-[oklch(0.15_0.02_65)]"
                          : "text-white/70 hover:text-white"
                      }`}
                    >
                      DE
                    </button>
                    <button
                      onClick={() => setLocale("en")}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                        selectedLang === "en"
                          ? "bg-gold text-[oklch(0.15_0.02_65)]"
                          : "text-white/70 hover:text-white"
                      }`}
                    >
                      EN
                    </button>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Right: Info */}
            <AnimatedSection delay={0.2}>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-sm font-semibold tracking-[0.15em] uppercase text-gold">
                  {t("buch.heroBadge")}
                </span>
              </div>

              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-[oklch(0.92_0.02_85)] mb-3">
                {BOOK_INFO[selectedLang].title}
              </h1>
              <p className="font-playfair text-xl md:text-2xl italic text-gold mb-2">
                {BOOK_INFO[selectedLang].subtitle.split(":")[0]}:
              </p>
              <p className="text-lg text-[oklch(0.7_0.02_85)] mb-6">
                {BOOK_INFO[selectedLang].subtitle.split(":")[1]}
              </p>

              <p className="text-[oklch(0.65_0.02_85)] mb-4">
                von <span className="text-[oklch(0.85_0.02_85)] font-semibold">{BOOK_INFO[selectedLang].authors}</span>
              </p>

              {/* Stars */}
              <div className="flex items-center gap-1 mb-8">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                ))}
                <span className="ml-2 text-sm text-[oklch(0.6_0.02_85)]">{t("buch.heroStarRating")}</span>
              </div>

              {/* Format Selection */}
              <div className="bg-[oklch(0.18_0.02_65)] rounded-2xl p-6 border border-gold/15">
                <p className="text-sm text-[oklch(0.65_0.02_85)] mb-3 font-semibold uppercase tracking-wider">{t("buch.heroFormatLabel")}</p>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <button
                    onClick={() => setSelectedFormat("ebook")}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      selectedFormat === "ebook"
                        ? "border-gold bg-gold/10"
                        : "border-[oklch(0.3_0.02_65)] hover:border-gold/40"
                    }`}
                  >
                    <Download className={`w-5 h-5 ${selectedFormat === "ebook" ? "text-gold" : "text-[oklch(0.5_0.02_85)]"}`} />
                    <div className="text-left">
                      <p className={`font-semibold text-sm ${selectedFormat === "ebook" ? "text-gold" : "text-[oklch(0.8_0.02_85)]"}`}>
                        {t("buch.heroEbookTitle")}
                      </p>
                      <p className="text-xs text-[oklch(0.5_0.02_85)]">{t("buch.heroEbookSub")}</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedFormat("print")}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      selectedFormat === "print"
                        ? "border-gold bg-gold/10"
                        : "border-[oklch(0.3_0.02_65)] hover:border-gold/40"
                    }`}
                  >
                    <Truck className={`w-5 h-5 ${selectedFormat === "print" ? "text-gold" : "text-[oklch(0.5_0.02_85)]"}`} />
                    <div className="text-left">
                      <p className={`font-semibold text-sm ${selectedFormat === "print" ? "text-gold" : "text-[oklch(0.8_0.02_85)]"}`}>
                        {t("buch.heroPrintTitle")}
                      </p>
                      <p className="text-xs text-[oklch(0.5_0.02_85)]">{t("buch.heroPrintSub")}</p>
                    </div>
                  </button>
                </div>

                {/* Price + CTA */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-3xl font-bold text-[oklch(0.92_0.02_85)]">{getPrice()}&euro;</span>
                    <span className="text-sm text-[oklch(0.5_0.02_85)] ml-1">{t("buch.heroVatNote")}</span>
                  </div>
                  <a
                    href={getStripeLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-semibold hover:bg-gold-light transition-all hover:scale-[1.02]"
                  >
                    {t("buch.heroBuyButton")} <ArrowRight className="w-4 h-4" />
                  </a>
                </div>

                {/* Trust Badges */}
                <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gold/10">
                  <div className="flex items-center gap-1.5 text-xs text-[oklch(0.55_0.02_85)]">
                    <ShieldCheck className="w-4 h-4 text-olive-light" />
                    {t("buch.heroBadgePayment")}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[oklch(0.55_0.02_85)]">
                    <Download className="w-4 h-4 text-olive-light" />
                    {selectedFormat === "ebook" ? t("buch.heroBadgeDownload") : t("buch.heroBadgeShipping")}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ========== BESCHREIBUNG ========== */}
      <section className="py-20">
        <div className="container max-w-4xl mx-auto px-4">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-olive mb-4">
                {t("buch.beschreibungBadge")}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)] mb-6">
                {t("buch.beschreibungTitle1")}{" "}
                <span className="italic text-olive">&ndash; {t("buch.beschreibungTitle2")}</span>
              </h2>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            <div className="prose prose-lg max-w-none text-[oklch(0.35_0.02_65)] leading-relaxed space-y-6">
              <p>{t("buch.beschreibungP1")}</p>
              <p>{t("buch.beschreibungP2")}</p>
              <p>{t("buch.beschreibungP3")}</p>
              <p className="text-[oklch(0.25_0.02_65)] font-semibold">{t("buch.beschreibungP4")}</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== KAPITEL HIGHLIGHTS ========== */}
      <section className="py-20 bg-[oklch(0.12_0.02_65)]">
        <div className="container max-w-5xl mx-auto px-4">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-gold mb-4">
                {t("buch.kapitelBadge")}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.92_0.02_85)]">
                {t("buch.kapitelTitle1")} <span className="italic text-gold">{t("buch.kapitelTitle2")}</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {CHAPTERS.map((ch, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="flex gap-5 p-6 rounded-2xl bg-[oklch(0.18_0.02_65)] border border-gold/10 hover:border-gold/25 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <ch.icon className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[oklch(0.9_0.02_85)] mb-1">{t(ch.titleKey)}</h3>
                    <p className="text-sm text-[oklch(0.6_0.02_85)]">{t(ch.descKey)}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== AUTOREN ========== */}
      <section className="py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-olive mb-4">
                {t("buch.autorenBadge")}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)]">
                Annam & Christopher <span className="italic text-olive">{t("buch.autorenTitle")}</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-10 items-center">
            <AnimatedSection>
              <img
                src={IMAGES.annamChris}
                alt="Annam & Christopher Hanfstein"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/5]"
              />
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="space-y-5 text-[oklch(0.35_0.02_65)] leading-relaxed">
                <p>
                  <span className="font-semibold text-[oklch(0.25_0.02_65)]">Annam Hanfstein</span>{" "}
                  {t("buch.autorenAnnam")}
                </p>
                <p>
                  <span className="font-semibold text-[oklch(0.25_0.02_65)]">Christopher Hanfstein</span>{" "}
                  {t("buch.autorenChristopher")}
                </p>
                <p className="font-semibold text-[oklch(0.25_0.02_65)]">
                  {t("buch.autorenTogether")}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ========== REVIEWS ========== */}
      <section className="py-20 bg-[oklch(0.96_0.008_85)]">
        <div className="container max-w-5xl mx-auto px-4">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-olive mb-4">
                {t("buch.reviewsBadge")}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)]">
                {t("buch.reviewsTitle1")} <span className="italic text-olive">{t("buch.reviewsTitle2")}</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6">
            {REVIEWS.map((review, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="p-6 rounded-2xl bg-white border border-[oklch(0.9_0.01_85)] h-full flex flex-col">
                  <Quote className="w-8 h-8 text-gold/30 mb-4" />
                  <p className="text-[oklch(0.35_0.02_65)] leading-relaxed flex-1 mb-4">
                    &ldquo;{t(review.textKey)}&rdquo;
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-[oklch(0.92_0.01_85)]">
                    <span className="font-semibold text-sm text-[oklch(0.3_0.02_65)]">{review.author}</span>
                    <div className="flex gap-0.5">
                      {[...Array(review.rating)].map((_, j) => (
                        <Star key={j} className="w-3.5 h-3.5 fill-gold text-gold" />
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== KAUFEN (Anchor) ========== */}
      <section id="kaufen" className="py-20 bg-[oklch(0.12_0.02_65)]">
        <div className="container max-w-4xl mx-auto px-4">
          <AnimatedSection>
            <div className="text-center mb-10">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-gold mb-4">
                {t("buch.kaufenBadge")}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.92_0.02_85)] mb-4">
                {t("buch.kaufenTitle")} <span className="italic text-gold">Der AYU</span>
              </h2>
              <p className="text-[oklch(0.6_0.02_85)] max-w-xl mx-auto">
                {t("buch.kaufenDesc")}
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">

              {/* E-Book Card */}
              <div className={`rounded-2xl p-8 border-2 transition-all cursor-pointer ${
                selectedFormat === "ebook"
                  ? "border-gold bg-gold/5 bg-[oklch(0.18_0.02_65)]"
                  : "border-[oklch(0.25_0.02_65)] bg-[oklch(0.16_0.02_65)] hover:border-gold/40"
              }`}
                onClick={() => setSelectedFormat("ebook")}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Download className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[oklch(0.92_0.02_85)]">{t("buch.ebookTitle")}</h3>
                    <p className="text-xs text-[oklch(0.55_0.02_85)]">{t("buch.ebookSub")}</p>
                  </div>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gold">19,90&euro;</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {[
                    t("buch.ebookFeature1"),
                    t("buch.ebookFeature2"),
                    t("buch.ebookFeature3"),
                    t("buch.ebookFeature4"),
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[oklch(0.65_0.02_85)]">
                      <ShieldCheck className="w-4 h-4 text-olive-light flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                {/* Language Selection for E-Book */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); setLocale("de"); setSelectedFormat("ebook"); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                      selectedLang === "de" && selectedFormat === "ebook"
                        ? "bg-gold text-[oklch(0.15_0.02_65)]"
                        : "bg-[oklch(0.25_0.02_65)] text-[oklch(0.7_0.02_85)] hover:bg-[oklch(0.3_0.02_65)]"
                    }`}
                  >
                    {t("buch.ebookLangDe")}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setLocale("en"); setSelectedFormat("ebook"); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                      selectedLang === "en" && selectedFormat === "ebook"
                        ? "bg-gold text-[oklch(0.15_0.02_65)]"
                        : "bg-[oklch(0.25_0.02_65)] text-[oklch(0.7_0.02_85)] hover:bg-[oklch(0.3_0.02_65)]"
                    }`}
                  >
                    {t("buch.ebookLangEn")}
                  </button>
                </div>

                <a
                  href={selectedFormat === "ebook" ? (selectedLang === "de" ? STRIPE_LINKS.ebookDe : STRIPE_LINKS.ebookEn) : "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-semibold hover:bg-gold-light transition-all"
                >
                  {t("buch.ebookBuyButton")} &rarr;
                </a>
              </div>

              {/* Print Card */}
              <div className={`rounded-2xl p-8 border-2 transition-all cursor-pointer ${
                selectedFormat === "print"
                  ? "border-gold bg-gold/5 bg-[oklch(0.18_0.02_65)]"
                  : "border-[oklch(0.25_0.02_65)] bg-[oklch(0.16_0.02_65)] hover:border-gold/40"
              }`}
                onClick={() => setSelectedFormat("print")}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[oklch(0.92_0.02_85)]">{t("buch.printTitle")}</h3>
                    <p className="text-xs text-[oklch(0.55_0.02_85)]">{t("buch.printSub")}</p>
                  </div>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gold">24,90&euro;</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {[
                    t("buch.printFeature1"),
                    t("buch.printFeature2"),
                    t("buch.printFeature3"),
                    t("buch.printFeature4"),
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[oklch(0.65_0.02_85)]">
                      <ShieldCheck className="w-4 h-4 text-olive-light flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="mb-4" /> {/* Spacer to align with ebook card */}

                <a
                  href={STRIPE_LINKS.print}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-semibold hover:bg-gold-light transition-all"
                >
                  {t("buch.printBuyButton")} &rarr;
                </a>
              </div>
            </div>
          </AnimatedSection>

          {/* Trust */}
          <AnimatedSection delay={0.3}>
            <div className="flex flex-wrap justify-center gap-8 mt-10">
              {[
                { icon: ShieldCheck, textKey: "buch.trustPayment" as const },
                { icon: Download, textKey: "buch.trustDownload" as const },
                { icon: Globe, textKey: "buch.trustGlobal" as const },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-[oklch(0.55_0.02_85)]">
                  <item.icon className="w-4 h-4 text-gold" />
                  {t(item.textKey)}
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-20">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <AnimatedSection>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)] mb-4">
              {t("buch.finalTitle1")} <span className="italic text-olive">{t("buch.finalTitle2")}</span>?
            </h2>
            <p className="text-[oklch(0.45_0.02_65)] mb-8 max-w-xl mx-auto">
              {t("buch.finalDesc")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#kaufen"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-olive text-white font-semibold hover:bg-olive-dark transition-all"
              >
                <BookOpen className="w-5 h-5" />
                {t("buch.finalBuyButton")}
              </a>
              <Link
                href="/termin"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-olive text-olive font-semibold hover:bg-olive/5 transition-all"
              >
                {t("buch.finalConsultButton")}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 bg-[oklch(0.12_0.02_65)] border-t border-gold/10">
        <div className="container max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={IMAGES.logo} alt="AYU" className="h-6 w-6" />
            <span className="text-sm text-[oklch(0.5_0.02_85)]">&copy; 2025 AYU-rveda. {t("buch.footerCopyright")}</span>
          </div>
          <div className="flex gap-6 text-sm text-[oklch(0.5_0.02_85)]">
            <Link href="/" className="hover:text-gold transition-colors">{t("buch.footerHome")}</Link>
            <Link href="/termin" className="hover:text-gold transition-colors">{t("buch.footerConsultation")}</Link>
            <Link href="/angebot" className="hover:text-gold transition-colors">{t("buch.footerOffer")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
